import { useLocation } from "react-router-dom";
import { Tab } from "@headlessui/react";
import GaugeChart from "react-gauge-chart";
import GraphRange from "components/GraphRange";

const Result = () => {
  const { result, callbackURL } = useLocation().state ?? {};

  if (typeof result === "undefined") {
    window.location.replace(callbackURL ?? "/");
    return null;
  } else
    return (
      <div className="px-6 py-8">
        <div className="py-6 text-center">
          <h1 className="text-primary  text-3xl font-medium">Result</h1>
          <p className="mt-2 text-xs font-light">
            {new Date(result.entry_time ?? Date.now()).toLocaleString(undefined, {
              dateStyle: "long",
              timeStyle: "medium",
            })}
          </p>
        </div>
        <Tab.Group manual>
          <Tab.List className="flex flex-wrap justify-center">
            {[
              "Key Body Vitals",
              "Heart Health",
              "Physiological",
              "Stress & ANS",
              "Health Score",
            ].map((title, index) => (
              <Tab
                key={`tab-header-${index}`}
                className={({ selected }) =>
                  `m-1 p-3 rounded-lg ${
                    selected ? "bg-primary text-white" : "bg-neutral-200 text-primary"
                  } text-sm outline-none`
                }
              >
                {title}
              </Tab>
            ))}
          </Tab.List>
          <Tab.Panels className="mt-6">
            <Tab.Panel>
              <h4 className="text-primary text-center text-lg font-medium">Key Body Vitals</h4>
              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="p-3 rounded-2xl border border-neutral-300 text-primary text-center">
                  <h3 className="text-xl font-semibold">{result.vitals?.heart_rate ?? "--"}</h3>
                  <h4 className="text-xs">bpm</h4>
                  <div className="mt-3">
                    <svg
                      style={{
                        marginLeft: `${Math.round(
                          (((result.vitals?.heart_rate ?? 20) - 20) / 160) * 100
                        )}%`,
                      }}
                      className="h-4 w-4 -translate-x-1/2"
                      fill="#000"
                      viewBox="0 0 16 16"
                    >
                      <path d="M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z" />
                    </svg>
                    <div className="h-6">
                      <GraphRange start={20} color="#efde66" width="25%" height="8px" />
                      <GraphRange start={60} color="#99c964" width="25%" height="8px" />
                      <GraphRange start={100} color="#efde66" width="25%" height="8px" />
                      <GraphRange start={140} end={220} color="#eb5b83" width="25%" height="8px" />
                    </div>
                  </div>
                  <p className="mt-1.5 text-sm text-primary-light font-light">Heart Rate</p>
                </div>
                <div className="p-3 rounded-2xl border border-neutral-300 text-primary text-center">
                  <h3 className="text-xl font-semibold">{result.vitals?.resp_rate ?? "--"}</h3>
                  <h4 className="text-xs">breathes/min</h4>
                  <div className="mt-3">
                    <svg
                      style={{
                        marginLeft: `${Math.round(((result.vitals?.resp_rate ?? 0) / 40) * 100)}%`,
                      }}
                      className="h-4 w-4 -translate-x-1/2"
                      fill="#000"
                      viewBox="0 0 16 16"
                    >
                      <path d="M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z" />
                    </svg>
                    <div className="h-6">
                      <GraphRange start={6} color="#efde66" width="25%" height="8px" />
                      <GraphRange start={12} color="#99c964" width="25%" height="8px" />
                      <GraphRange start={20} color="#efde66" width="25%" height="8px" />
                      <GraphRange start={30} color="#eb5b83" width="25%" height="8px" />
                    </div>
                  </div>
                  <p className="mt-1.5 text-sm text-primary-light font-light">Resp Rate</p>
                </div>
                <div className="p-3 rounded-2xl border border-neutral-300 text-primary text-center">
                  <h3 className="text-xl font-semibold">{result.vitals?.oxy_sat_prcnt ?? "--"}</h3>
                  <h4 className="text-xs">%</h4>
                  <div className="mt-3">
                    <svg
                      style={{
                        marginLeft: `${Math.round(
                          (((result.vitals?.oxy_sat_prcnt ?? 95) - 85) / 15) * 100
                        )}%`,
                      }}
                      className="h-4 w-4 -translate-x-1/2"
                      fill="#000"
                      viewBox="0 0 16 16"
                    >
                      <path d="M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z" />
                    </svg>
                    <div className="h-6">
                      <GraphRange start={0} color="#eb5b83" width="34%" height="8px" />
                      <GraphRange start={90} color="#efde66" width="33%" height="8px" />
                      <GraphRange start={95} color="#99c964" width="33%" height="8px" />
                    </div>
                  </div>
                  <p className="mt-1.5 text-sm text-primary-light font-light">Oxygen Sat</p>
                </div>
                <div className="p-3 rounded-2xl border border-neutral-300 text-primary text-center">
                  <h3 className="text-xl font-semibold">
                    {result.vitals?.bp_sys ?? "--"} / {result.vitals?.bp_dia ?? "--"}
                  </h3>
                  <h4 className="text-xs">mmHg</h4>
                  <div className="mt-3">
                    <svg
                      style={{
                        marginLeft: `${Math.round(
                          (((result.vitals?.bp_sys ?? 80) - 80) / 125) * 100
                        )}%`,
                      }}
                      className="h-4 w-4 -translate-x-1/2"
                      fill="#000"
                      viewBox="0 0 16 16"
                    >
                      <path d="M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z" />
                    </svg>
                    <div className="h-6">
                      <GraphRange start={80} color="#eb5b83" width="14%" height="8px" />
                      <GraphRange start={100} color="#efde66" width="14%" height="8px" />
                      <GraphRange start={110} color="#99c964" width="14%" height="8px" />
                      <GraphRange start={120} color="#efde66" width="15%" height="8px" />
                      <GraphRange start={140} color="#edb868" width="15%" height="8px" />
                      <GraphRange start={160} color="#eb5b83" width="14%" height="8px" />
                      <GraphRange start={180} color="#b73d5f" width="14%" height="8px" />
                    </div>
                    <p className="text-sm">SYS</p>
                  </div>
                  <div className="mt-3">
                    <svg
                      style={{
                        marginLeft: `${(((result.vitals?.bp_dia ?? 40) - 40) / 80) * 100}%`,
                      }}
                      className="h-4 w-4 -translate-x-1/2"
                      fill="#000"
                      viewBox="0 0 16 16"
                    >
                      <path d="M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z" />
                    </svg>
                    <div className="h-6">
                      <GraphRange start={40} color="#b73d5f" width="12.5%" height="8px" />
                      <GraphRange start={50} color="#eb5b83" width="12.5%" height="8px" />
                      <GraphRange start={60} color="#edb868" width="12.5%" height="8px" />
                      <GraphRange start={70} color="#99c964" width="12.5%" height="8px" />
                      <GraphRange start={80} color="#efde66" width="12.5%" height="8px" />
                      <GraphRange start={90} color="#edb868" width="12.5%" height="8px" />
                      <GraphRange start={100} color="#eb5b83" width="12.5%" height="8px" />
                      <GraphRange start={110} color="#b73d5f" width="12.5%" height="8px" />
                    </div>
                    <p className="text-sm">DIA</p>
                  </div>
                  <p className="mt-1.5 text-sm text-primary-light font-light">Blood Pressure</p>
                </div>
              </div>
            </Tab.Panel>
            <Tab.Panel>
              <h4 className="text-primary text-center text-lg font-medium">Heart Health</h4>
              <h6 className="text-primary text-center text-sm font-light">
                Heart Rate Variability
              </h6>
              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="p-3 rounded-2xl border border-neutral-300 text-primary text-center">
                  <h3 className="text-xl font-semibold">
                    {result.metadata?.heart_scores?.sdnn?.toFixed?.(2) ?? "--"}
                  </h3>
                  <h4 className="text-xs">msec</h4>
                  <div className="mt-3">
                    <svg
                      style={{
                        marginLeft: `${Math.round(
                          (result.metadata?.heart_scores?.sdnn?.toFixed?.(2) ?? 100) <= 97
                            ? (result.metadata.heart_scores.sdnn - 2).toFixed(2)
                            : "95"
                        )}%`,
                      }}
                      className="h-4 w-4 -translate-x-1/2"
                      fill="#000"
                      viewBox="0 0 16 16"
                    >
                      <path d="M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z" />
                    </svg>
                    <div className="h-6">
                      <GraphRange start="Low" color="#efde66" width="30%" height="8px" />
                      <GraphRange start="Normal" color="#99c964" width="50%" height="8px" />
                      <GraphRange start="High" color="#059b78" width="20%" height="8px" />
                    </div>
                  </div>
                  <p className="mt-1.5 text-sm text-primary-light font-light">SDNN</p>
                </div>
                <div className="p-3 rounded-2xl border border-neutral-300 text-primary text-center">
                  <h3 className="text-xl font-semibold">
                    {result.metadata?.heart_scores?.rmssd?.toFixed?.(2) ?? "--"}
                  </h3>
                  <h4 className="text-xs">msec</h4>
                  <div className="mt-3">
                    <svg
                      style={{
                        marginLeft: `${Math.round(
                          (result.metadata?.heart_scores?.rmssd?.toFixed?.(2) ?? 100) <= 90
                            ? result.metadata.heart_scores.rmssd.toFixed(2)
                            : "95"
                        )}%`,
                      }}
                      className="h-4 w-4 -translate-x-1/2"
                      fill="#000"
                      viewBox="0 0 16 16"
                    >
                      <path d="M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z" />
                    </svg>
                    <div className="h-6">
                      <GraphRange start="Low" color="#efde66" width="20%" height="8px" />
                      <GraphRange start="Normal" color="#99c964" width="60%" height="8px" />
                      <GraphRange start="High" color="#059b78" width="20%" height="8px" />
                    </div>
                  </div>
                  <p className="mt-1.5 text-sm text-primary-light font-light">RMSSD</p>
                </div>
                <div className="p-3 rounded-2xl border border-neutral-300 text-primary text-center">
                  <h3 className="text-xl font-semibold">
                    {result.metadata?.heart_scores?.pNN50_per?.toFixed?.(2) ?? "--"}
                  </h3>
                  <h4 className="text-xs">%</h4>
                  <div className="mt-3">
                    <svg
                      style={{
                        marginLeft: `${Math.round(
                          (result.metadata?.heart_scores?.pNN50_per?.toFixed?.(2) ?? 100) <= 90
                            ? result.metadata.heart_scores.pNN50_per.toFixed(2)
                            : "95"
                        )}%`,
                      }}
                      className="h-4 w-4 -translate-x-1/2"
                      fill="#000"
                      viewBox="0 0 16 16"
                    >
                      <path d="M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z" />
                    </svg>
                    <div className="h-6">
                      <GraphRange start="Low" color="#efde66" width="10%" height="8px" />
                      <GraphRange start="Normal" color="#99c964" width="70%" height="8px" />
                      <GraphRange start="High" color="#059b78" width="20%" height="8px" />
                    </div>
                  </div>
                  <p className="mt-1.5 text-sm text-primary-light font-light">PNN50</p>
                </div>
                <div className="p-3 rounded-2xl border border-neutral-300 text-primary text-center">
                  <h3 className="text-xl font-semibold">
                    {result.metadata?.cardiovascular?.cardiac_out?.toFixed?.(2) ?? "--"}
                  </h3>
                  <h4 className="text-xs">L/min</h4>
                  <div className="mt-3">
                    <svg
                      style={{
                        marginLeft: `${Math.round(
                          ((result.metadata?.cardiovascular?.cardiac_out ?? 0) / 10) * 100
                        )}%`,
                      }}
                      className="h-4 w-4 -translate-x-1/2"
                      fill="#000"
                      viewBox="0 0 16 16"
                    >
                      <path d="M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z" />
                    </svg>
                    <div className="h-6">
                      <GraphRange start="Low" color="#efde66" width="40%" height="8px" />
                      <GraphRange start="Normal" color="#99c964" width="40%" height="8px" />
                      <GraphRange start="High" color="#eb5b83" width="20%" height="8px" />
                    </div>
                  </div>
                  <p className="mt-1.5 text-sm text-primary-light font-light">Cardiac Output</p>
                </div>
                <div className="p-3 rounded-2xl border border-neutral-300 text-primary text-center">
                  <h3 className="text-xl font-semibold">
                    {result.metadata?.cardiovascular?.map?.toFixed?.(2) ?? "--"}
                  </h3>
                  <h4 className="text-xs">mm/Hg</h4>
                  <div className="mt-3">
                    <svg
                      style={{
                        marginLeft: `${Math.round(result.metadata?.cardiovascular?.map ?? 0)}%`,
                      }}
                      className="h-4 w-4 -translate-x-1/2"
                      fill="#000"
                      viewBox="0 0 16 16"
                    >
                      <path d="M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z" />
                    </svg>
                    <div className="h-6">
                      <GraphRange start="Low" color="#efde66" width="40%" height="8px" />
                      <GraphRange start="Normal" color="#99c964" width="40%" height="8px" />
                      <GraphRange start="High" color="#eb5b83" width="20%" height="8px" />
                    </div>
                  </div>
                  <p className="mt-1.5 text-sm text-primary-light font-light">
                    Mean Arterial Pressure
                  </p>
                </div>
                <div className="p-3 rounded-2xl border border-neutral-300 text-primary text-center">
                  <h3 className="text-xl font-semibold">
                    {result.metadata?.heart_scores?.heart_utilized ?? "--"}
                  </h3>
                  <h4 className="text-xs">%</h4>
                  <div className="mt-3">
                    <svg
                      style={{
                        marginLeft: `${Math.round(
                          (((result.metadata?.heart_scores?.heart_utilized ?? 25) - 25) / 76) * 100
                        )}%`,
                      }}
                      className="h-4 w-4 -translate-x-1/2"
                      fill="#000"
                      viewBox="0 0 16 16"
                    >
                      <path d="M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z" />
                    </svg>
                    <div className="h-6">
                      <GraphRange start={25} color="#efde66" width="34%" height="8px" />
                      <GraphRange start={50} color="#99c964" width="33%" height="8px" />
                      <GraphRange start={75} color="#eb5b83" width="33%" height="8px" />
                    </div>
                  </div>
                  <p className="mt-1.5 text-sm text-primary-light font-light">Heart Utilised</p>
                </div>
                <div className="p-3 rounded-2xl border border-neutral-300 text-primary text-center">
                  <h3 className="text-xl font-semibold">
                    {result.metadata?.heart_scores?.HRMax ?? "--"}
                  </h3>
                  <h4 className="text-xs">bpm</h4>
                  <p className="mt-1.5 text-justify text-xs text-primary-light">
                    <svg className="inline h-4 w-4 mr-1" viewBox="0 0 48 48">
                      <circle fill="#2196F3" cx="24" cy="24" r="21"></circle>
                      <rect x="22" y="22" fill="#fff" width="4" height="11"></rect>
                      <circle fill="#fff" cx="24" cy="16.5" r="2.5"></circle>
                    </svg>
                    <span>
                      It is possible to exceed the upper limit of your zone without any ill effects,
                      as long as you do not have coronary artery disease or are at risk for a heart
                      attack.
                    </span>
                  </p>
                  <p className="mt-1.5 text-sm text-primary-light font-light">Heart Rate Max</p>
                </div>
                <div className="p-3 rounded-2xl border border-neutral-300 text-primary text-center">
                  <h3 className="text-xl font-semibold">
                    {result.metadata?.heart_scores?.HRR ?? "--"}
                  </h3>
                  <h4 className="text-xs">bpm</h4>
                  <p className="mt-1.5 text-sm text-primary-light font-light">Heart Rate Reserve</p>
                </div>
                <div className="sm:col-span-2 grid grid-cols-2 gap-3">
                  <div className="p-3 rounded-2xl border border-neutral-300 text-primary text-center">
                    <h3 className="text-xl font-semibold">
                      {result.metadata?.heart_scores?.THRR ?? "--"}
                    </h3>
                    <h4 className="text-xs">bpm</h4>
                    <p className="mt-1.5 text-sm text-primary-light font-light">Target HR Range</p>
                  </div>
                  <div className="p-3 rounded-2xl border border-neutral-300 text-primary text-center">
                    <h3 className="text-xl font-semibold">
                      {result.metadata?.physiological_scores?.vo2max ?? "--"}
                    </h3>
                    <h4 className="text-xs">mL/kg/min</h4>
                    <p className="mt-1.5 text-sm text-primary-light font-light">
                      VO<sub>2</sub> Max
                    </p>
                  </div>
                </div>
              </div>
            </Tab.Panel>
            <Tab.Panel>
              <h4 className="text-primary text-center text-lg font-medium">Physiological Scores</h4>
              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="p-3 rounded-2xl border border-neutral-300 text-primary text-center">
                  <h3 className="text-xl font-semibold">
                    {result.metadata?.physiological_scores?.bmi ?? "--"}
                  </h3>
                  <h4 className="text-xs">
                    kg/m<sup>2</sup>
                  </h4>
                  <div className="mt-3">
                    <svg
                      style={{
                        marginLeft: `${
                          (result.metadata?.physiological_scores?.bmi ?? 100) <= 40
                            ? Math.round(
                                ((result.metadata.physiological_scores.bmi - 15) / 25) * 100
                              )
                            : "95"
                        }%`,
                      }}
                      className="h-4 w-4 -translate-x-1/2"
                      fill="#000"
                      viewBox="0 0 16 16"
                    >
                      <path d="M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z" />
                    </svg>
                    <div className="h-6">
                      <GraphRange start="0" color="#efde66" width="15%" height="8px" />
                      <GraphRange start="18.5" color="#99c964" width="25%" height="8px" />
                      <GraphRange start="25" color="#efde66" width="20%" height="8px" />
                      <GraphRange start="30" color="#edb868" width="20%" height="8px" />
                      <GraphRange start=">35" color="#eb5b83" width="20%" height="8px" />
                    </div>
                  </div>
                  <p className="mt-1.5 text-sm text-primary-light font-light">BMI</p>
                </div>
                <div className="p-3 rounded-2xl border border-neutral-300 text-primary text-center">
                  <h3 className="text-xl font-semibold">
                    {result.metadata?.physiological_scores?.bloodvolume ?? "--"}
                  </h3>
                  <h4 className="text-xs">ml</h4>
                  <p className="mt-1.5 text-sm text-primary-light font-light">Blood Volume</p>
                </div>
                <div className="sm:col-span-2 grid grid-cols-2 gap-3">
                  <div className="p-3 rounded-2xl border border-neutral-300 text-primary text-center">
                    <h3 className="text-xl font-semibold">
                      {result.metadata?.physiological_scores?.tbw ?? "--"}
                    </h3>
                    <h4 className="text-xs">Ltrs</h4>
                    <p className="mt-1.5 text-sm text-primary-light font-light">Total Body Water</p>
                  </div>
                  <div className="p-3 rounded-2xl border border-neutral-300 text-primary text-center">
                    <h3 className="text-xl font-semibold">
                      {result.metadata?.physiological_scores?.tbwp ?? "--"}
                    </h3>
                    <h4 className="text-xs">%</h4>
                    <p className="mt-1.5 text-sm text-primary-light font-light">
                      Body Water Percentage
                    </p>
                  </div>
                  <div className="p-3 rounded-2xl border border-neutral-300 text-primary text-center">
                    <h3 className="text-xl font-semibold">
                      {result.metadata?.physiological_scores?.bodyfat ?? "--"}
                    </h3>
                    <h4 className="text-xs">%</h4>
                    <p className="mt-1.5 text-sm text-primary-light font-light">Total Body Fat</p>
                  </div>
                  {(result.metadata?.physiological_scores?.cal_carb ?? "--") !== "--" && (
                    <div className="p-3 rounded-2xl border border-neutral-300 text-primary text-center">
                      <h3 className="text-xl font-semibold">
                        {result.metadata.physiological_scores.cal_carb}
                      </h3>
                      <h4 className="text-xs">%</h4>
                      <p className="mt-1.5 text-sm text-primary-light font-light">
                        Calorie from Carbs
                      </p>
                    </div>
                  )}
                  {(result.metadata?.physiological_scores?.cal_fat ?? "--") !== "--" && (
                    <div className="p-3 rounded-2xl border border-neutral-300 text-primary text-center">
                      <h3 className="text-xl font-semibold">
                        {result.metadata.physiological_scores.cal_fat}
                      </h3>
                      <h4 className="text-xs">%</h4>
                      <p className="mt-1.5 text-sm text-primary-light font-light">
                        Calorie from Fat
                      </p>
                    </div>
                  )}
                  <div className="p-3 rounded-2xl border border-neutral-300 text-primary text-center">
                    <h3 className="text-xl font-semibold">
                      {result.metadata?.physiological_scores?.weight ?? "--"}
                    </h3>
                    <h4 className="text-xs">Kgs</h4>
                    <p className="mt-1.5 text-sm text-primary-light font-light">Body Weight</p>
                  </div>
                </div>
              </div>
            </Tab.Panel>
            <Tab.Panel>
              <h4 className="text-primary text-center text-lg font-medium">Stress &amp; ANS</h4>
              <div className="mt-4 space-y-4">
                <div className="mx-auto max-w-lg p-3 rounded-2xl border border-neutral-300 text-primary text-center">
                  <h3 className="text-xl font-semibold">
                    {result.metadata?.heart_scores?.stress_index?.toFixed?.(2) ?? "--"}
                  </h3>
                  {(result.metadata?.heart_scores?.stress_index ?? "--") !== "--" &&
                    (result.metadata.heart_scores.stress_index < 0.5 ? (
                      <div className="mt-3 text-[#32b509]">
                        <svg className="h-16 w-16 mx-auto" viewBox="0 0 400 400">
                          <path
                            d="M175.000 1.183 C 10.270 21.534,-58.998 223.923,58.539 341.461 C 176.367 459.289,378.987 389.487,398.870 224.219 C 414.431 94.879,304.345 -14.797,175.000 1.183 M225.781 26.172 C 368.256 47.808,425.676 222.072,323.874 323.874 C 213.468 434.280,24.656 356.114,24.656 200.000 C 24.656 107.917,92.304 34.567,186.328 24.699 C 192.711 24.029,217.851 24.967,225.781 26.172 M112.752 132.837 C 90.451 136.851,75.957 153.075,85.234 163.641 C 90.289 169.399,96.560 169.309,104.472 163.365 C 116.245 154.521,127.503 154.773,139.333 164.145 C 148.903 171.727,160.337 166.941,160.337 155.354 C 160.337 141.994,133.205 129.155,112.752 132.837 M269.655 132.855 C 254.106 135.636,239.663 146.471,239.663 155.354 C 239.663 166.941,251.097 171.727,260.667 164.145 C 272.497 154.773,283.755 154.521,295.528 163.365 C 303.440 169.309,309.711 169.399,314.766 163.641 C 327.537 149.095,296.661 128.024,269.655 132.855 M88.269 210.053 C 76.941 216.126,82.862 254.847,99.217 281.656 C 145.124 356.903,254.876 356.903,300.783 281.656 C 317.138 254.847,323.059 216.126,311.731 210.053 C 307.481 207.774,92.519 207.774,88.269 210.053 M292.182 234.180 C 292.179 234.932,291.498 238.359,290.668 241.797 C 273.255 313.958,183.814 338.067,132.556 284.415 C 119.751 271.012,107.869 246.884,107.818 234.180 C 107.813 232.885,112.721 232.813,200.000 232.813 C 287.279 232.813,292.187 232.885,292.182 234.180"
                            fill="currentColor"
                          />
                        </svg>
                        <p className="mt-2 text-lg font-medium">LOW</p>
                      </div>
                    ) : result.metadata.heart_scores.stress_index < 1.5 ? (
                      <div className="mt-3 text-[#61b80d]">
                        <svg className="h-16 w-16 mx-auto" viewBox="0 0 400 400">
                          <path
                            d="M175.000 1.154 C 12.262 22.498,-58.093 219.616,54.824 337.860 C 167.277 455.618,365.586 397.642,396.768 237.891 C 422.654 105.270,308.695 -16.381,175.000 1.154 M222.656 27.046 C 340.067 41.816,409.380 171.201,356.485 276.858 C 291.850 405.965,109.347 406.737,44.215 278.177 C -18.674 154.045,83.962 9.599,222.656 27.046 M113.219 125.801 C 93.803 129.022,85.782 152.936,99.049 168.046 C 105.556 175.457,118.610 178.101,127.854 173.879 C 154.903 161.528,142.547 120.937,113.219 125.801 M278.125 125.802 C 253.430 129.843,249.254 163.426,272.146 173.879 C 281.390 178.101,294.444 175.457,300.951 168.046 C 316.918 149.861,301.823 121.925,278.125 125.802 M98.626 243.746 C 92.203 247.662,91.028 255.850,95.760 263.718 C 141.928 340.493,256.455 341.088,303.491 264.798 C 310.779 252.978,306.505 242.193,294.531 242.193 C 288.612 242.193,286.113 243.948,280.974 251.711 C 241.829 310.847,158.171 310.847,119.026 251.711 C 112.725 242.193,105.683 239.443,98.626 243.746"
                            fill="currentColor"
                          />
                        </svg>
                        <p className="mt-2 text-lg font-medium">NORMAL</p>
                      </div>
                    ) : result.metadata.heart_scores.stress_index < 5 ? (
                      <div className="mt-3 text-[#eea508]">
                        <svg className="h-16 w-16 mx-auto" viewBox="0 0 400 400">
                          <path
                            d="M175.000 1.154 C 12.212 22.505,-58.087 219.622,54.893 337.932 C 167.277 455.618,365.598 397.585,396.768 237.891 C 422.654 105.270,308.695 -16.381,175.000 1.154 M222.656 27.046 C 340.063 41.816,409.371 171.192,356.484 276.858 C 291.865 405.964,109.346 406.735,44.215 278.177 C -18.674 154.045,83.962 9.599,222.656 27.046 M113.219 125.801 C 93.803 129.022,85.782 152.936,99.049 168.046 C 105.556 175.457,118.610 178.101,127.854 173.879 C 154.903 161.528,142.547 120.937,113.219 125.801 M278.125 125.802 C 253.430 129.843,249.254 163.426,272.146 173.879 C 281.390 178.101,294.444 175.457,300.951 168.046 C 316.918 149.861,301.823 121.925,278.125 125.802 M115.921 260.497 C 106.643 264.536,106.440 278.782,115.595 283.398 L 119.081 285.156 200.000 285.156 L 280.919 285.156 284.405 283.398 C 293.651 278.736,293.340 264.405,283.906 260.464 C 280.083 258.867,119.595 258.898,115.921 260.497"
                            fill="currentColor"
                          />
                        </svg>
                        <p className="mt-2 text-lg font-medium">ELEVATED</p>
                      </div>
                    ) : result.metadata.heart_scores.stress_index < 9 ? (
                      <div className="mt-3 text-[#f31e2e]">
                        <svg className="h-16 w-16 mx-auto" viewBox="0 0 400 400">
                          <path
                            d="M175.000 1.154 C 12.262 22.498,-58.093 219.616,54.824 337.860 C 167.277 455.618,365.586 397.642,396.768 237.891 C 422.654 105.270,308.695 -16.381,175.000 1.154 M222.656 27.046 C 340.067 41.816,409.380 171.201,356.485 276.858 C 291.850 405.965,109.347 406.737,44.215 278.177 C -18.674 154.045,83.962 9.599,222.656 27.046 M113.219 125.801 C 93.803 129.022,85.782 152.936,99.049 168.046 C 105.556 175.457,118.610 178.101,127.854 173.879 C 154.903 161.528,142.547 120.937,113.219 125.801 M278.125 125.802 C 253.430 129.843,249.254 163.426,272.146 173.879 C 281.390 178.101,294.444 175.457,300.951 168.046 C 316.918 149.861,301.823 121.925,278.125 125.802 M275.391 245.948 C 263.490 247.326,118.007 273.727,116.067 274.860 C 105.609 280.968,109.349 297.326,121.413 298.240 C 126.722 298.642,279.654 271.782,283.570 269.759 C 296.010 263.334,289.478 244.316,275.391 245.948"
                            fill="currentColor"
                          />
                        </svg>
                        <p className="mt-2 text-lg font-medium">HIGH</p>
                      </div>
                    ) : (
                      <div className="mt-3 text-[#b4020f]">
                        <svg className="h-16 w-16 mx-auto" viewBox="0 0 400 400">
                          <path
                            d="M186.653 1.189 C 180.429 2.701,174.011 5.704,168.308 9.773 L 163.367 13.299 158.441 10.641 C 136.127 -1.400,109.571 6.153,98.334 27.734 L 95.893 32.422 91.095 32.168 C 69.058 31.001,50.510 48.969,50.510 71.484 C 50.510 97.120,73.887 115.677,98.799 109.817 C 103.314 108.755,103.493 108.774,104.587 110.415 C 113.817 124.258,132.582 129.628,151.367 123.801 L 153.125 123.255 153.121 137.604 L 153.117 151.953 138.238 160.938 C 130.055 165.879,123.112 169.863,122.810 169.791 C 122.507 169.718,116.129 165.912,108.637 161.332 C 83.403 145.907,85.927 145.898,60.439 161.490 L 46.268 170.159 30.941 160.811 C 14.336 150.683,11.980 149.810,7.988 152.307 C -0.932 157.886,-2.807 212.407,4.790 245.313 C 32.384 364.835,162.265 431.896,276.172 385.435 C 292.906 378.609,319.078 363.097,321.008 358.860 C 323.645 353.074,320.152 346.875,314.255 346.875 C 311.767 346.875,309.556 347.961,302.281 352.758 C 186.827 428.882,32.750 356.923,17.135 219.584 C 16.152 210.942,16.568 179.876,17.728 175.185 L 18.359 172.635 29.419 179.351 C 46.171 189.523,46.260 189.516,68.360 176.144 C 77.211 170.788,84.588 166.406,84.753 166.406 C 84.919 166.406,91.435 170.306,99.233 175.073 C 124.029 190.229,122.106 190.184,145.261 176.148 L 161.593 166.248 178.057 176.223 C 201.176 190.231,199.040 190.271,223.454 175.361 L 238.409 166.226 254.754 176.139 C 277.912 190.182,275.974 190.227,300.767 175.073 C 308.565 170.306,315.081 166.406,315.247 166.406 C 315.412 166.406,322.789 170.788,331.640 176.144 C 353.743 189.518,353.863 189.528,370.608 179.361 L 381.641 172.662 382.260 175.198 C 383.423 179.964,383.851 210.912,382.873 219.539 C 379.907 245.693,369.829 275.578,357.451 294.930 C 351.324 304.507,352.989 311.715,361.382 311.952 C 365.659 312.074,367.569 310.373,372.489 302.062 C 394.667 264.599,404.184 219.214,398.861 176.303 C 395.137 146.290,393.787 145.616,368.490 161.151 L 353.778 170.187 339.584 161.504 C 314.069 145.896,316.599 145.905,291.363 161.332 C 283.871 165.912,277.501 169.718,277.208 169.791 C 276.914 169.863,269.972 165.879,261.781 160.938 L 246.888 151.953 246.881 137.604 L 246.875 123.255 248.633 123.801 C 267.418 129.628,286.183 124.258,295.413 110.415 C 296.507 108.774,296.686 108.755,301.201 109.817 C 326.113 115.677,349.490 97.120,349.490 71.484 C 349.490 48.969,330.942 31.001,308.905 32.168 L 304.107 32.422 301.666 27.734 C 290.438 6.169,263.873 -1.398,241.594 10.622 L 236.703 13.260 231.438 9.639 C 218.636 0.835,201.459 -2.406,186.653 1.189 M207.213 16.812 C 213.204 17.569,221.555 22.263,227.599 28.272 C 234.993 35.621,237.474 35.713,244.973 28.917 C 260.724 14.643,283.948 21.353,289.476 41.774 C 291.781 50.290,294.580 51.880,303.288 49.622 C 322.740 44.576,339.198 62.865,331.108 80.537 C 322.580 99.164,297.044 98.730,288.955 79.820 C 286.549 74.194,284.735 72.656,280.506 72.656 C 269.932 72.656,269.724 85.311,280.110 96.807 L 282.821 99.808 279.649 102.980 C 270.069 112.559,256.843 112.350,246.650 102.458 C 241.858 97.808,236.398 97.227,233.135 101.020 L 231.250 103.212 231.250 127.206 L 231.250 151.199 215.625 160.840 L 200.000 170.481 184.375 160.840 L 168.750 151.199 168.750 138.866 L 168.750 126.533 170.898 127.134 C 188.655 132.099,206.064 120.482,206.898 103.111 C 207.510 90.377,192.756 88.127,191.028 100.690 C 188.982 115.565,170.393 115.565,168.347 100.690 C 166.908 90.230,152.344 89.945,152.344 100.376 C 152.344 102.529,151.872 104.079,150.977 104.866 C 142.540 112.283,128.808 111.437,120.351 102.980 L 117.179 99.808 120.005 96.680 C 127.303 88.602,129.697 78.188,125.183 74.155 C 120.167 69.673,114.628 71.584,111.459 78.890 C 102.901 98.624,77.621 99.602,68.892 80.537 C 60.791 62.840,77.232 44.569,96.712 49.622 C 105.420 51.880,108.219 50.290,110.524 41.774 C 116.052 21.353,139.276 14.643,155.027 28.917 C 162.533 35.719,165.004 35.624,172.425 28.247 C 180.764 19.958,192.547 15.052,201.563 16.115 C 202.637 16.242,205.180 16.555,207.213 16.812 M138.704 40.415 C 136.693 42.427,136.586 42.888,136.852 48.423 C 138.445 81.608,189.680 81.608,191.273 48.423 C 191.885 35.690,177.131 33.439,175.403 46.002 C 173.357 60.877,154.768 60.877,152.722 46.002 C 151.693 38.523,143.759 35.361,138.704 40.415 M220.911 57.748 C 215.322 62.742,218.412 70.072,228.115 74.838 C 241.106 81.218,262.109 74.253,262.109 63.564 C 262.109 55.914,255.192 53.348,247.656 58.203 C 244.581 60.184,242.537 60.938,240.234 60.938 C 237.932 60.938,235.888 60.184,232.813 58.203 C 227.455 54.751,224.396 54.634,220.911 57.748 M105.726 205.374 C 79.656 215.037,77.787 251.501,102.734 263.720 C 113.864 269.171,129.779 266.406,138.717 257.467 C 162.053 234.132,136.595 193.932,105.726 205.374 M272.913 205.374 C 246.844 215.037,244.974 251.501,269.922 263.720 C 281.052 269.171,296.967 266.406,305.905 257.467 C 329.241 234.132,303.782 193.932,272.913 205.374 M123.273 221.792 C 137.240 228.920,132.108 250.000,116.406 250.000 C 100.523 250.000,95.630 229.082,109.766 221.613 C 113.037 219.885,119.710 219.973,123.273 221.792 M290.461 221.792 C 304.427 228.920,299.295 250.000,283.594 250.000 C 267.711 250.000,262.818 229.082,276.953 221.613 C 280.225 219.885,286.897 219.973,290.461 221.792 M182.422 287.252 C 152.995 291.009,127.161 303.509,116.350 319.222 C 111.464 326.323,114.280 333.594,121.915 333.594 C 125.911 333.594,125.466 333.917,132.493 325.905 C 159.174 295.483,240.826 295.483,267.507 325.905 C 274.534 333.917,274.089 333.594,278.085 333.594 C 285.752 333.594,288.536 326.324,283.610 319.164 C 268.178 296.734,222.065 282.191,182.422 287.252 M335.762 324.655 C 330.334 327.601,330.905 336.654,336.669 339.042 C 345.184 342.569,352.312 331.808,345.495 325.717 C 342.618 323.146,339.225 322.776,335.762 324.655"
                            fill="currentColor"
                          />
                        </svg>
                        <p className="mt-2 text-lg font-medium">VERY HIGH</p>
                      </div>
                    ))}
                  <p className="mt-1.5 text-sm text-primary-light font-light">Stress Index</p>
                </div>
                <div className="mx-auto max-w-lg p-3 rounded-2xl border border-neutral-300 text-primary text-center">
                  {(result.metadata?.heart_scores?.rmssd ?? "--") !== "--" ? (
                    result.metadata.heart_scores.rmssd < 65 ? (
                      result.vitals.heart_rate > 100 ||
                      result.vitals.bp_sys > 130 ||
                      result.vitals.bp_dia > 90 ? (
                        <>
                          <h3 className="text-xl font-semibold">More Sympathetic Interactions</h3>
                          <p className="mt-1.5 text-justify text-xs text-primary-light">
                            Although HRV manifests as a function of your heart rate, it actually
                            originates from your nervous system. Your autonomic nervous system,
                            which controls the involuntary aspects of your physiology, has two
                            branches, parasympathetic (deactivating) and sympathetic (activating).
                          </p>
                          <p className="mt-1.5 text-justify text-xs text-primary-light">
                            The <b>sympathetic nervous system</b> (often called “fight or flight”)
                            reflects responses to things like stress and exercise, and increases
                            your heart rate and blood pressure.
                          </p>
                        </>
                      ) : (
                        <>
                          <h3 className="text-xl font-semibold">
                            More Parasympathetic Interactions
                          </h3>
                          <p className="mt-1.5 text-justify text-xs text-primary-light">
                            Although HRV manifests as a function of your heart rate, it actually
                            originates from your nervous system. Your autonomic nervous system,
                            which controls the involuntary aspects of your physiology, has two
                            branches, parasympathetic (deactivating) and sympathetic (activating).
                          </p>
                          <p className="mt-1.5 text-justify text-xs text-primary-light">
                            The <b>parasympathetic nervous system</b> (often referred to as “rest
                            and digest”) handles inputs from internal organs, like digestion or your
                            fingernails and hair growing.
                          </p>
                        </>
                      )
                    ) : (
                      <>
                        <h3 className="text-xl font-semibold">Balanced ANS Interactions</h3>
                        <p className="mt-1.5 text-justify text-xs text-primary-light">
                          When you have high heart rate variability, it means that your body is
                          responsive to both sets of inputs (parasympathetic and sympathetic). This
                          is a sign that your nervous system is balanced, and that your body is very
                          capable of adapting to its environment and performing at its best.
                        </p>
                      </>
                    )
                  ) : (
                    <p className="text-justify text-xs text-primary-light">
                      Not enought data to process. This happens when the signal strength of the scan
                      is poor due to improper light or too much movements.
                    </p>
                  )}
                  <p className="mt-1.5 text-sm text-primary-light font-light">
                    Autonomic Nervous Systems (ANS)
                  </p>
                </div>
              </div>
            </Tab.Panel>
            <Tab.Panel>
              <h4 className="text-primary text-center text-lg font-medium">Health Score</h4>
              <div className="mt-4 mx-auto max-w-lg p-3 rounded-2xl border border-neutral-300 text-primary text-center">
                <GaugeChart
                  nrOfLevels={4}
                  arcsLength={[0.2, 0.2, 0.2, 0.4]}
                  arcWidth={0.2}
                  percent={(result.health_risk_score ?? 0) * 0.01}
                  colors={["#32b509", "#a0dd0c", "#eea508", "#f31e2e"]}
                  needleColor="#aeaeae"
                  textColor="currentColor"
                />
                {(result.health_risk_score ?? "--") !== "--" &&
                  (result.health_risk_score <= 20 ? (
                    <h3 className="text-[#32b509] text-lg font-medium">LOW</h3>
                  ) : result.health_risk_score <= 40 ? (
                    <h3 className="text-[#a0dd0c] text-lg font-medium">MEDIUM</h3>
                  ) : result.health_risk_score <= 60 ? (
                    <h3 className="text-[#eea508] text-lg font-medium">HIGH</h3>
                  ) : (
                    <h3 className="text-[#f31e2e] text-lg font-medium">VERY HIGH</h3>
                  ))}
                <p className="mt-1.5 text-sm text-primary-light font-light">Health Risk Score</p>
              </div>
            </Tab.Panel>
          </Tab.Panels>
        </Tab.Group>
        <div className="mt-8 px-4 pt-6 pb-8 rounded-2xl bg-primary-light text-white text-center">
          <h4>Disclaimer:</h4>
          <p className="mt-2 text-xs leading-normal">
            In case of an emergency, please contact your physicial. This data is only for monitoring
            purpose and should not be considered as a medical device.
          </p>
          <button
            type="button"
            onClick={() => window.location.replace(callbackURL ?? "/")}
            className="block mt-4 mx-auto px-4 py-2 w-48 rounded-full border border-current text-lg font-medium"
          >
            Go back to App
          </button>
        </div>
      </div>
    );
};

export default Result;
